import { Alert, Button, Descriptions, Modal, Spin, Tag, Typography } from 'antd'
import dayjs from 'dayjs'
import {
  AdminMediaCapture,
  canRetryTranscription,
  formatCostUsd,
  formatDurationSeconds,
  formatTokens,
  isPartialTranscript,
  isVideoCapture,
  PARTIAL_TRANSCRIPT_EXPLANATION,
  purposeLabels,
  transcriptionStatusColors,
  transcriptionStatusLabels,
  transcriptPlaceholder,
} from './transcriptsModel'
import { useAudioLinkQuery, useRetryTranscriptionMutation } from './transcriptsQueries'

type Props = {
  capture: AdminMediaCapture | null
  onClose: () => void
  onOpenChat: (sessionId: string) => void
}

const TranscriptDetailModal = ({ capture, onClose, onOpenChat }: Props) => {
  const audioLinkQuery = useAudioLinkQuery(capture ? { id: capture.id, tenantId: capture.tenantId } : null)
  const retryMutation = useRetryTranscriptionMutation()

  if (!capture) {
    return null
  }

  const canRetry = canRetryTranscription(capture)

  return (
    <Modal
      open
      onCancel={onClose}
      footer={null}
      width={720}
      title="Media capture"
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
    >
      <Descriptions column={2} size="small" bordered>
        <Descriptions.Item label="Status">
          <Tag color={transcriptionStatusColors[capture.transcriptionStatus]}>
            {transcriptionStatusLabels[capture.transcriptionStatus] ?? capture.transcriptionStatus}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Purpose">{purposeLabels[capture.purpose] ?? capture.purpose}</Descriptions.Item>
        <Descriptions.Item label="Tenant">{capture.tenantId}</Descriptions.Item>
        <Descriptions.Item label="Created by">{capture.createdByEmail}</Descriptions.Item>
        <Descriptions.Item label="Created">{dayjs(capture.createdAtUtc).format('MMM D, YYYY h:mm A')}</Descriptions.Item>
        <Descriptions.Item label="Duration">{formatDurationSeconds(capture.durationSeconds)}</Descriptions.Item>
        <Descriptions.Item label="Job">{capture.jobId ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Customer">{capture.customerId ?? '—'}</Descriptions.Item>
        <Descriptions.Item label="Tokens (in / out)">{formatTokens(capture.inputTokens, capture.outputTokens)}</Descriptions.Item>
        <Descriptions.Item label="Cost">{formatCostUsd(capture.transcriptionCostUsd)}</Descriptions.Item>
        <Descriptions.Item label="Attempts">{capture.transcriptionAttempts}</Descriptions.Item>
        <Descriptions.Item label="Started">
          {capture.transcriptionStartedAtUtc
            ? dayjs(capture.transcriptionStartedAtUtc).format('MMM D, YYYY h:mm A')
            : '—'}
        </Descriptions.Item>
        {capture.transcriptionFailureReason && (
          <Descriptions.Item label="Failure reason" span={2}>
            <Typography.Text type="danger">{capture.transcriptionFailureReason}</Typography.Text>
          </Descriptions.Item>
        )}
        <Descriptions.Item label="Capture ID" span={2}>
          <Typography.Text copyable style={{ fontFamily: 'monospace', fontSize: 12 }}>{capture.id}</Typography.Text>
        </Descriptions.Item>
      </Descriptions>

      <div style={{ marginTop: 16 }}>
        {audioLinkQuery.isLoading && <Spin />}
        {audioLinkQuery.data &&
          (isVideoCapture(capture.mimeType) ? (
            <video controls src={audioLinkQuery.data} style={{ width: '100%', maxHeight: '50vh' }} />
          ) : (
            <audio controls src={audioLinkQuery.data} style={{ width: '100%' }} />
          ))}
      </div>

      <div style={{ marginTop: 16 }}>
        <Typography.Title level={5}>Transcript</Typography.Title>
        {isPartialTranscript(capture) && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="This transcript is incomplete"
            description={PARTIAL_TRANSCRIPT_EXPLANATION}
          />
        )}
        {capture.transcript ? (
          <Typography.Paragraph style={{ whiteSpace: 'pre-wrap' }}>{capture.transcript}</Typography.Paragraph>
        ) : (
          <Typography.Text type="secondary">{transcriptPlaceholder(capture)}</Typography.Text>
        )}
      </div>

      <div style={{ marginTop: 16, display: 'flex', gap: 8 }}>
        {capture.sessionId && (
          <Button type="primary" onClick={() => onOpenChat(capture.sessionId!)}>
            View chat session
          </Button>
        )}
        {canRetry && (
          <Button
            loading={retryMutation.isLoading}
            onClick={() => retryMutation.mutate({ id: capture.id, tenantId: capture.tenantId })}
          >
            Retry transcription
          </Button>
        )}
      </div>
    </Modal>
  )
}

export default TranscriptDetailModal
